// src/web/features/materials/pages/admin/edit/[id].tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { ValidatedInput } from '@voilajsx/uikit/form';
import {
  Edit,
  FileText,
  AlertCircle,
  CheckCircle,
  Lock,
  Unlock
} from 'lucide-react';
import { config } from '../../../../auth/config';
import { SEO } from '../../../../../shared/components';

interface MaterialData {
  id: number;
  title: string;
  description: string;
  subject: string;
  classSection: string;
  accessType: 'free' | 'restricted';
  fileName: string;
  fileUrl: string;
}

interface MaterialOptions {
  classes: string[];
  subjects: string[];
}

const EditMaterialPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    classSection: '',
    accessType: 'free' as 'free' | 'restricted'
  });
  const [material, setMaterial] = useState<MaterialData | null>(null);
  const [options, setOptions] = useState<MaterialOptions>({ classes: [], subjects: [] });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch predefined options (classes and subjects)
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`${config.api.baseUrl}/api/materials/options`);
        if (response.ok) {
          const data = await response.json();
          setOptions({
            classes: data.classes || [],
            subjects: data.subjects || []
          });
        }
      } catch (err) {
        console.error('Error fetching options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  // Fetch material data on component mount
  useEffect(() => {
    const fetchMaterial = async () => {
      if (!id) {
        setError('Material ID is required');
        setFetchLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('auth_token');
        if (!token) throw new Error('User is not authenticated');

        const response = await fetch(`${config.api.baseUrl}/api/materials/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Material not found');
        }

        const data = await response.json();
        const materialData = data.material;

        setMaterial(materialData);
        setFormData({
          title: materialData.title || '',
          description: materialData.description || '',
          subject: materialData.subject || '',
          classSection: materialData.classSection || '',
          accessType: materialData.accessType || 'free'
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch material');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchMaterial();
  }, [id]);

  const handleChange = (value: string, fieldId?: string) => {
    if (!fieldId) return;
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const validate = () => {
    return formData.title.trim() !== '' &&
           formData.subject.trim() !== '' &&
           formData.classSection.trim() !== '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('User is not authenticated');

      const response = await fetch(`${config.api.baseUrl}/api/materials/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      setSuccess(true);
      setTimeout(() => navigate('/materials'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update material');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading material...</p>
        </div>
      </div>
    );
  }

  if (error && !material) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button 
          onClick={() => navigate('/materials')} 
          className="mt-4"
          variant="outline"
        >
          Back to Materials
        </Button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Edit Material - Admin Panel"
        description="Edit educational material details"
      />
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <Edit className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Edit Material
                </h1>
                <p className="mt-1 text-orange-100 text-sm">
                  Update the details of "{material?.title}"
                </p>
              </div>
            </div>
          </div>

          {/* Edit Form Card */}
          <Card className="border-t-4 border-t-orange-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FileText className="h-5 w-5 text-orange-600" />
                </div>
                Material Information
              </CardTitle>
              <CardDescription className="mt-1.5">
                Update the material details (file cannot be changed)
              </CardDescription>
            </div>
            
            <CardContent className="p-6">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-6 border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success</AlertTitle>
                  <AlertDescription className="text-green-700">
                    Material updated successfully. Redirecting...
                  </AlertDescription>
                </Alert>
              )}

              {/* Current File Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="font-medium text-gray-900 mb-2">Current File</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">File:</span> {material?.fileName}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Note: To change the file, please delete this material and upload a new one.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ValidatedInput
                    id="title"
                    label="Material Title"
                    required
                    value={formData.title}
                    onChange={(value) => handleChange(value, 'title')}
                    placeholder="Enter material title"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) => handleChange(e.target.value, 'subject')}
                      required
                      disabled={optionsLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                    >
                      <option value="">
                        {optionsLoading ? 'Loading...' : 'Select Subject'}
                      </option>
                      {options.subjects.map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Class <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.classSection}
                      onChange={(e) => handleChange(e.target.value, 'classSection')}
                      required
                      disabled={optionsLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                    >
                      <option value="">
                        {optionsLoading ? 'Loading...' : 'Select Class'}
                      </option>
                      {options.classes.map((className) => (
                        <option key={className} value={className}>
                          {className}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Access Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.accessType === 'free'
                          ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="accessType"
                          value="free"
                          checked={formData.accessType === 'free'}
                          onChange={(e) => handleChange(e.target.value, 'accessType')}
                          className="sr-only"
                        />
                        <Unlock className={`h-5 w-5 ${formData.accessType === 'free' ? 'text-green-600' : 'text-gray-400'}`} />
                        <div>
                          <span className={`font-medium ${formData.accessType === 'free' ? 'text-green-700' : 'text-gray-700'}`}>
                            Free
                          </span>
                          <p className="text-xs text-gray-500">Public access</p>
                        </div>
                      </label>

                      <label className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.accessType === 'restricted'
                          ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="accessType"
                          value="restricted"
                          checked={formData.accessType === 'restricted'}
                          onChange={(e) => handleChange(e.target.value, 'accessType')}
                          className="sr-only"
                        />
                        <Lock className={`h-5 w-5 ${formData.accessType === 'restricted' ? 'text-amber-600' : 'text-gray-400'}`} />
                        <div>
                          <span className={`font-medium ${formData.accessType === 'restricted' ? 'text-amber-700' : 'text-gray-700'}`}>
                            Premium
                          </span>
                          <p className="text-xs text-gray-500">Contact required</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <ValidatedInput
                  id="description"
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(value) => handleChange(value, 'description')}
                  placeholder="Brief description about the material"
                />

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={loading || optionsLoading}
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Edit className="mr-2 h-4 w-4" />
                        Update Material
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/materials')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EditMaterialPage;
